/**
 * useEmergencyStatus Hook
 * React hook for monitoring emergency shutdown status in real-time
 * 
 * This hook addresses the critical security vulnerability where emergency shutdown
 * did not immediately update UI state for logged-in users.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { emergencyStatusService, type EmergencyStatusEvent, type EmergencyStatusState } from '../services/emergencyStatusService';

export interface UseEmergencyStatusReturn {
  // Status flags
  isEmergencyActive: boolean;
  isAIBlocked: boolean;
  blockingLevel: 'none' | 'emergency' | 'global' | 'provider_api';
  blockingReason: string | null;
  
  // Status information
  lastChecked: Date;
  isMonitoring: boolean;
  
  // Actions
  forceCheck: () => Promise<void>;
  reportBlockedAccess: (action: string, reason?: string) => void;
  
  // Raw status for advanced use cases
  rawStatus: EmergencyStatusState;
}

export interface UseEmergencyStatusOptions {
  // Show toast notifications for status changes
  showToastNotifications?: boolean;
  
  // Custom callback for status changes
  onStatusChange?: (event: EmergencyStatusEvent) => void;
  
  // Custom callback for blocked access attempts
  onAccessBlocked?: (action: string, reason: string) => void;
  
  // Enable debug logging
  enableDebugLogging?: boolean;
}

/**
 * Hook for monitoring emergency shutdown status
 */
export function useEmergencyStatus(options: UseEmergencyStatusOptions = {}): UseEmergencyStatusReturn {
  const {
    showToastNotifications = true,
    onStatusChange,
    onAccessBlocked,
    enableDebugLogging = false
  } = options;

  // State
  const [status, setStatus] = useState<EmergencyStatusState>(() => emergencyStatusService.getStatus());
  const [isMonitoring, setIsMonitoring] = useState(false);
  
  // Refs for stable callbacks
  const onStatusChangeRef = useRef(onStatusChange);
  const onAccessBlockedRef = useRef(onAccessBlocked);
  
  // Update refs when callbacks change
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
    onAccessBlockedRef.current = onAccessBlocked;
  }, [onStatusChange, onAccessBlocked]);

  // Debug logging helper
  const debugLog = useCallback((message: string, data?: any) => {
    if (enableDebugLogging && process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log(`[useEmergencyStatus] ${message}`, data || '');
    }
  }, [enableDebugLogging]);

  // Handle status change events
  const handleStatusChange = useCallback((event: EmergencyStatusEvent) => {
    debugLog('Status change event received', event);
    
    // Update local status
    setStatus(emergencyStatusService.getStatus());

    // Show toast notifications if enabled
    if (showToastNotifications) {
      switch (event.type) {
        case 'status_change':
          if (event.status === 'emergency_shutdown') {
            toast.error(
              'Emergency Shutdown Active: AI services have been disabled',
              { 
                toastId: 'emergency-shutdown',
                autoClose: false,
                closeButton: true
              }
            );
          } else if (event.status === 'disabled') {
            toast.warning(
              event.message || 'AI services are temporarily unavailable',
              { 
                toastId: 'ai-services-disabled',
                autoClose: 8000
              }
            );
          } else if (event.status === 'active') {
            // Clear any existing emergency notifications
            toast.dismiss('emergency-shutdown');
            toast.dismiss('ai-services-disabled');
            
            toast.success(
              'AI services have been restored and are now operational',
              { 
                toastId: 'ai-services-restored',
                autoClose: 5000
              }
            );
          }
          break;

        case 'websocket_disconnection':
          toast.error(
            'Connection lost due to emergency shutdown',
            {
              toastId: 'emergency-disconnection',
              autoClose: false,
              closeButton: true
            }
          );
          break;

        case 'access_blocked':
          toast.warning(
            event.message || 'AI access blocked',
            {
              toastId: 'access-blocked',
              autoClose: 5000
            }
          );
          break;
      }
    }

    // Call custom status change handler
    if (onStatusChangeRef.current) {
      onStatusChangeRef.current(event);
    }
  }, [showToastNotifications, debugLog]);

  // Initialize emergency status monitoring
  useEffect(() => {
    debugLog('Initializing emergency status monitoring');
    
    let isActive = true;
    
    const initialize = async () => {
      try {
        setIsMonitoring(true);
        
        // Initialize the service if not already done
        await emergencyStatusService.initialize();
        
        // Add event listener
        const unsubscribe = emergencyStatusService.addListener(handleStatusChange);
        
        // Update initial status
        if (isActive) {
          setStatus(emergencyStatusService.getStatus());
        }
        
        // Cleanup function
        return () => {
          debugLog('Cleaning up emergency status monitoring');
          unsubscribe();
          setIsMonitoring(false);
        };
      } catch (error) {
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.error('[useEmergencyStatus] Failed to initialize:', error);
        }
        
        if (isActive) {
          setIsMonitoring(false);
          // Show error notification
          if (showToastNotifications) {
            toast.error(
              'Unable to monitor emergency status - AI services may be restricted',
              { 
                toastId: 'monitoring-error',
                autoClose: false,
                closeButton: true
              }
            );
          }
        }
      }
    };
    
    initialize().then(cleanup => {
      if (!isActive) {
        cleanup?.();
      } else {
        // Store cleanup function to run on unmount
        return cleanup;
      }
    });

    // Cleanup on unmount
    return () => {
      isActive = false;
      debugLog('useEmergencyStatus hook unmounting');
    };
  }, [handleStatusChange, showToastNotifications, debugLog]);

  // Force status check
  const forceCheck = useCallback(async () => {
    debugLog('Forcing emergency status check');
    
    try {
      const newStatus = await emergencyStatusService.forceStatusCheck();
      setStatus(newStatus);
    } catch (error) {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.error('[useEmergencyStatus] Force check failed:', error);
      }
      
      if (showToastNotifications) {
        toast.error('Unable to check emergency status', {
          toastId: 'force-check-error',
          autoClose: 5000
        });
      }
    }
  }, [debugLog, showToastNotifications]);

  // Report blocked access
  const reportBlockedAccess = useCallback((action: string, reason?: string) => {
    debugLog('Reporting blocked access', { action, reason });
    
    emergencyStatusService.reportBlockedAccess(action, reason);
    
    // Call custom blocked access handler
    if (onAccessBlockedRef.current) {
      onAccessBlockedRef.current(action, reason || 'Access blocked');
    }
  }, [debugLog]);

  // Return hook interface
  return {
    // Status flags
    isEmergencyActive: status.isEmergencyActive,
    isAIBlocked: status.blockingLevel !== 'none',
    blockingLevel: status.blockingLevel,
    blockingReason: status.blockingReason || null,
    
    // Status information
    lastChecked: status.lastChecked,
    isMonitoring,
    
    // Actions
    forceCheck,
    reportBlockedAccess,
    
    // Raw status
    rawStatus: status
  };
}

/**
 * Simplified hook that just returns blocking status
 * Useful for components that only need to know if AI is blocked
 */
export function useAIBlockingStatus(): boolean {
  const { isAIBlocked } = useEmergencyStatus({ 
    showToastNotifications: false,
    enableDebugLogging: false
  });
  
  return isAIBlocked;
}

/**
 * Hook for components that need to block specific actions
 * Returns a function that checks if action is allowed and reports if blocked
 */
export function useAIActionGuard(): (action: string) => boolean {
  const { isAIBlocked, blockingReason, reportBlockedAccess } = useEmergencyStatus({
    showToastNotifications: false,
    enableDebugLogging: false
  });

  return useCallback((action: string) => {
    if (isAIBlocked) {
      reportBlockedAccess(action, blockingReason || 'AI services unavailable');
      return false;
    }
    return true;
  }, [isAIBlocked, blockingReason, reportBlockedAccess]);
}